<template>
  <JsonLDModel :jsonOptions="jsonOptionsModel" />
  <NuxtLayout>
    <div class="slot-body">
      <h1 v-if="dadosSSR?.nameModel" class="ssr-model-title">
        {{ (route.params.language === 'pt-br' ? 'Lives de ' : 'Lives of ') + dadosSSR.nameModel }}
      </h1>
      <NavBarHeader
        :model-info="dadosSSR"
        :model-data="viewModel"
        v-if="viewModel"
      >
        <div class="tab-panels hide-scrollbar">
          <div class="tab-panel">
            <!-- O player só existe com a resposta do cliente: a URL de
                 reprodução nunca passa pelo servidor -->
            <Live
              v-if="visaoAssinante"
              :model="dadosSSR"
              :videosModel="reproduzivel"
            />
            <LiveNotSubscribe
              v-else
              :key="versaoLives"
              :model="dadosSSR"
              :videosModel="viewModel"
              :somente-publico="!reproduzivel"
              :aguardando-versao="aguardandoVersao"
              @plans="(value: any) => showModalSubscribePlans(value)"
            />
            <div class="block block--gray">
              <ClientOnly>
                <BannerAssineHorizontal :path="bannerAssineHorizontal" />
              </ClientOnly>
            </div>
          </div>
        </div>
      </NavBarHeader>
      <ModalSubscribePlans
        @showModalSubscribePlans="showModalSubscribePlans"
        :showSubscribe="showSubscribe"
        :plans="storePlan.plans"
        v-if="storePlan.plans"
      />
      <ModalLiveMessage
        :showModalLive="showModalLive"
        @closeModalLive="closeModalLive"
      />
    </div>
  </NuxtLayout>
</template>
<script lang="ts" setup>
// SEO: página de conteúdo explícito — marcação que o Google pede para não
// classificar o site inteiro como adulto. Ver composables/useAdultRating.ts
useAdultRating();
import Live from "~/components/Model/Lives.vue";
import LiveNotSubscribe from "@/components/Model/LiveNotSubscribe.vue";
import BannerAssineHorizontal from "@/components/HomePage/BannerAssineHorizontal.vue";
import NavBarHeader from "@/components/Model/NavBarHeader.vue";
import api from "@/api";
import { isMobile } from "@/shared/utils/mobile";
import ModalSubscribePlans from "@/components/Modais/ModalSubscribePlansAndCredits.vue";
import ModalLiveMessage from "~/components/Modais/ModalLiveMessage.vue";
import JsonLDModel from "@/components/JsonLDModel.vue";
import { publicLivesPayload } from "@/utils/publicModelPayload";
import { ehVisaoAssinanteLive, respostaLivesValida } from "@/utils/ensaioSSR";

definePageMeta({
  middleware: ["not-found-lang"],
});

const storePlan = planStore();
const route = useRoute();
const storeUser = userStore();

const ensaio = useEnsaioRota();
const urlLives = `v2/modelos/${ensaio.nome}/lives?${ensaio.query}`;

const [metadados, livesSSR] = await Promise.all([
  useEnsaioMetadados(),
  useEnsaioPublico("model-lives-public", `v2/modelos/${ensaio.nome}/lives`, publicLivesPayload),
]);
exigirMetadados(metadados.error.value, metadados.data.value);

const dadosSSR = ref(metadados.data.value);

const canonical = useModelSubpageCanonical(
  route.params.name,
  route.params.language,
  "live",
);
const seo = await useSeoConfig(String(route.name), String(route.params.language));
useHead({
  templateParams: {
    subTitle: null,
    subPage: (dadosSSR.value.nameModel ? dadosSSR.value.nameModel + ": " : "") + (seo.value?.title || (route.params.language === 'pt-br' ? "Lives Exclusivas e Interações" : "Exclusive Lives and Interactions")),
    page_url: canonical,
    page_description:
      (dadosSSR.value.nameModel ? dadosSSR.value.nameModel + ": " : "") + (seo.value?.description || (route.params.language === 'pt-br' ? "Participe das lives exclusivas no Bella da Semana. Interaja ao vivo com a modelo e veja conteúdo inédito. Assine e não perca!" : "Join exclusive live streams on Bella Club. Interact live with the model and see never-before-seen content. Subscribe and don't miss out!")),
    page_image: shareImage(seo.value?.image, dadosSSR.value.imgPerfil),
    page_keywords:
      seo.value?.keywords ||
      (dadosSSR.value.meta_keywords ??
      "bella, bella da semana, bela da semana, bella semana, bela semana, belladasemana, bella site, bella da semana site, bella blog"),
    page_locale: route.params.language as string,
    page_url_pt:
      "https://belladasemana.com.br/pt-br/modelos/" +
      route.params.name +
      "/live",
    page_url_en:
      "https://belladasemana.com.br/en/modelos/" + route.params.name + "/live",
    page_canonical: canonical,
  },
});

const bannerAssineHorizontal = ref("");
const showSubscribe = ref(false);
const showModalLive = ref(false);

// `reproduzivel` é a resposta do cliente (com a URL de reprodução e o crachá);
// até ela chegar, a página mostra a casca pública do SSR.
const reproduzivel = ref<any>(null);
const viewModel = computed(() => reproduzivel.value ?? livesSSR.data.value);
// Só o dado do cliente decide: o SSR é sempre anônimo.
const visaoAssinante = computed(() => ehVisaoAssinanteLive(reproduzivel.value));
const versaoLives = ref(0);
// Até a resposta do cliente chegar não se sabe quem é o usuário: sem o convite
// de prévia, que é do anônimo, e com spinner no player, já no HTML do SSR.
const aguardandoVersao = ref(true);

const jsonOptionsModel = ref({
  type: "Person",
  name: dadosSSR.value.nameModel || dadosSSR.value.seoTitleModel || "",
  jobTitle: 'Model',
  image: dadosSSR.value.imgPerfil ?? "",
  url: useModelCanonical(route.params.name, route.params.language)
});

onMounted(() => {
  getBannerPath();
  carregarLives();
});

// Assinante sem o plano das lives recebe a prévia: avisa. Só a resposta do
// cliente conta, porque a do SSR é sempre a do anônimo.
watch(reproduzivel, (newVal) => {
  if (storeUser.isActiveUser && newVal?.liveVideos[0]?.name == "preview")
    showModalLive.value = true;
});

function closeModalLive() {
  showModalLive.value = false;
}

async function carregarLives() {
  try {
    const dados = await buscarEnsaioNoCliente(urlLives);
    if (!respostaLivesValida(dados)) return;
    reproduzivel.value = dados;
    versaoLives.value++;
  } finally {
    // Se a busca falhar, fica a casca pública, como para o anônimo.
    aguardandoVersao.value = false;
  }
}

function getBannerPath() {
  if (!sessionStorage.getItem("bannerAssineHorizontal"))
    api
      .get(
        `v1/home/getBannerSuper?mobile=${isMobile()}&language=${
          route.params.language
        }`
      )
      .then(({ data }) => {
        bannerAssineHorizontal.value = data.bannerAssineHorizontal;
        sessionStorage.setItem(
          "bannerAssineHorizontal",
          JSON.stringify(bannerAssineHorizontal.value)
        );
      });
  else
    bannerAssineHorizontal.value = JSON.parse(
      sessionStorage.getItem("bannerAssineHorizontal") ?? ""
    );
}

function showModalSubscribePlans(value: boolean) {
  if (!storeUser.isActiveUser) {
    showSubscribe.value = value;
    if (value == true)
      showModalPlans(
        "Clicar para reproduzir o video sem ter uma assinatura ativa"
      );
  }
}

function showModalPlans(acionador: string) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "paywall",
    paywall: { acionador: acionador },
  });
}
</script>
<style scoped lang="css">
.tab-panels :deep(.tab-panel) {
  width: 98%;
}
.slot-body {
  margin-top: 65px !important;
  background-color: #fff !important;
}

.title {
  font-family: "Bebas-Neue-Light";
  font-size: 60px;
  font-weight: 500;
  color: black;
}

.title-models {
  font-family: "Exo-2";
  font-size: 32px;
  font-weight: 600;
  color: #b20000;
}

.subtitle {
  font-size: 24px;
  font-weight: 600;
}

.ssr-model-title {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* regras mobile 769vp */
@media screen and (max-width: 769px) {
}
</style>
